import type { SelectChangeEvent } from '@mui/material'
import {
  Box,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { FilePdfOutlined } from '@ant-design/icons'
import Paper from 'components/@extended/Paper'
import EmployeeRewardsContent from 'components/EmployeeRewardsContent'
import SimpleTitle from 'components/SimpleTitle'
import { useSetBreadcrumbActions } from 'contexts/BreadcrumbActionsContext'
import useAuth from 'hooks/useAuth'
import { useFetchAllUserRewardReports } from 'hooks/useUserRewards'
import { useFetchOrganization } from 'hooks/useOrganizations'
import { usePeriods } from 'hooks/usePeriods'
import { useCreateEngagement } from 'hooks/useEngagements'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getRootOrganizationId } from 'types/user-profile'
import { findPeriodById } from 'types/periods'
import { analytics } from 'utils/analytics'

const EmployeeRewardsDashboard: React.FC = () => {
  const theme = useTheme()
  const { token, profile } = useAuth()
  const { userId } = useParams()
  const setBreadcrumbActions = useSetBreadcrumbActions()

  const [rootOrganizationId, setRootOrganizationId] = useState('')
  const [selectedReportIndex, setSelectedReportIndex] = useState<number>(0)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const { createEngagement } = useCreateEngagement(token!)

  // Get all reward reports for user
  const { reports, isLoading: reportsLoading } = useFetchAllUserRewardReports(
    userId || '',
    rootOrganizationId,
    token!,
    !!userId && !!rootOrganizationId && !!token
  )

  // Get periods for labels
  const { periods: periodsResponse } = usePeriods(token!, rootOrganizationId, !!rootOrganizationId && !!token)
  const periodsData = useMemo(() => periodsResponse?.periods || [], [periodsResponse])

  // Get organization for logo
  const { organization } = useFetchOrganization(rootOrganizationId, token!)

  // Get selected report
  const selectedReport = useMemo(() => {
    if (!reports || reports.length === 0) return null
    return reports[selectedReportIndex] || reports[0]
  }, [reports, selectedReportIndex])

  // Helper to get period label from periodId
  const getPeriodLabel = useCallback(
    (periodId: string) => {
      if (!periodsData.length) return periodId
      const period = findPeriodById(periodsData, periodId)
      return period?.label || periodId
    },
    [periodsData]
  )

  // Get period label from selected report
  const selectedPeriodLabel = useMemo(() => {
    if (!selectedReport?.periodId) return ''
    return getPeriodLabel(selectedReport.periodId)
  }, [selectedReport, getPeriodLabel])

  // Get user name
  const userName = useMemo(() => {
    if (selectedReport?.firstName && selectedReport?.lastName) {
      return `${selectedReport.firstName} ${selectedReport.lastName}`
    }
    return 'Employee'
  }, [selectedReport?.firstName, selectedReport?.lastName])

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Track employee rewards dashboard view
  useEffect(() => {
    if (reports && reports.length > 0 && rootOrganizationId && userId && profile) {
      createEngagement({
        userId: profile.user.id,
        rootOrganizationId: rootOrganizationId,
        eventType: 'employee_rewards_dashboard_view',
        viewedRecordId: selectedReport?.id || null,
        viewedRecordUserId: userId,
        viewedRecordType: 'user_rewards',
        eventData: null,
      })

      analytics.track('employee_rewards_dashboard_viewed', {
        userId: profile?.user.id,
        viewedForUserId: userId,
      })
    }
  }, [reports, rootOrganizationId, userId])

  const handleReportChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedReportIndex(Number(event.target.value))
  }, [])

  const handleDownloadPdf = useCallback(async () => {
    if (!contentRef.current) return

    setIsGeneratingPdf(true)
    try {
      // Use current theme background for PDF export
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: theme.palette.background.default,
      })

      const imgData = canvas.toDataURL('image/png')

      // Add padding around the content for print margins
      const padding = 80
      const pdfWidth = canvas.width + padding * 2
      const pdfHeight = canvas.height + padding * 2

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [pdfWidth, pdfHeight],
      })

      // Use current theme background color for PDF
      const bgColor = theme.palette.background.default
      const r = parseInt(bgColor.slice(1, 3), 16)
      const g = parseInt(bgColor.slice(3, 5), 16)
      const b = parseInt(bgColor.slice(5, 7), 16)
      pdf.setFillColor(r, g, b)
      pdf.rect(0, 0, pdfWidth, pdfHeight, 'F')

      // Add the image with padding offset
      pdf.addImage(imgData, 'PNG', padding, padding, canvas.width, canvas.height)

      const fileName = `${userName.replace(/\s+/g, '_')}_Total_Rewards_${selectedPeriodLabel.replace(/\s+/g, '_')}.pdf`
      pdf.save(fileName)
    } catch (error) {
      console.error('Failed to generate PDF:', error)
    } finally {
      setIsGeneratingPdf(false)
    }
  }, [userName, selectedPeriodLabel, theme])

  // Show loading while waiting for required data
  const isLoading = !rootOrganizationId || reportsLoading

  // Check if user has no rewards
  const hasNoRewards =
    !reports || reports.length === 0 || !selectedReport?.categories || selectedReport.categories.length === 0

  // Memoize breadcrumb actions to prevent infinite re-renders
  const breadcrumbActions = useMemo(() => {
    if (isLoading || hasNoRewards) return null

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* PDF Download Button */}
        <Tooltip title="Download as PDF">
          <IconButton
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            {isGeneratingPdf ? <CircularProgress size={20} color="inherit" /> : <FilePdfOutlined />}
          </IconButton>
        </Tooltip>
      </Box>
    )
  }, [isLoading, hasNoRewards, isGeneratingPdf, theme, handleDownloadPdf])

  // Set breadcrumb actions (must be before early returns)
  useEffect(() => {
    setBreadcrumbActions(breadcrumbActions)

    // Clear breadcrumb actions on unmount
    return () => {
      setBreadcrumbActions(null)
    }
  }, [breadcrumbActions, setBreadcrumbActions])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (hasNoRewards) {
    return (
      <>
        <Grid container pb={2} spacing={2}>
          {/* Title */}
          <Grid size={{ xs: 12, md: reports && reports.length > 1 ? 10 : 12 }}>
            <SimpleTitle title={`${userName}'s Total Rewards`} />
          </Grid>

          {/* Report Selector - only shown if multiple reports */}
          {reports && reports.length > 1 && (
            <Grid
              size={{ xs: 12, md: 2 }}
              sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 0, md: 1 } }}
            >
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  shrink
                  variant="outlined"
                  sx={{
                    '&.MuiInputLabel-shrink': {
                      background: theme.palette.background.default,
                    },
                  }}
                >
                  Period
                </InputLabel>
                <Select
                  labelId="report-select-label"
                  id="report-select"
                  variant="outlined"
                  value={String(selectedReportIndex)}
                  label="Period"
                  style={{ borderRadius: '21px' }}
                  onChange={handleReportChange}
                >
                  {reports.map((report, index) => (
                    <MenuItem key={report.periodId} value={String(index)}>
                      {getPeriodLabel(report.periodId)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper elevation={0}>
            <Typography variant="h5">No rewards package found</Typography>
            <Typography variant="body1">
              You do not have a rewards package assigned for this period. Please contact your manager or HR department
              for assistance.
            </Typography>
          </Paper>
        </Grid>
      </>
    )
  }

  return (
    <>
      <Grid container pb={2} spacing={2}>
        {/* Title */}
        <Grid size={{ xs: 12, md: reports && reports.length > 1 ? 10 : 12 }}>
          <SimpleTitle title={`${userName}'s Total Rewards`} />
        </Grid>

        {/* Report Selector - only shown if multiple reports */}
        {reports && reports.length > 1 && (
          <Grid
            size={{ xs: 12, md: 2 }}
            sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 0, md: 1 } }}
          >
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                shrink
                variant="outlined"
                sx={{
                  '&.MuiInputLabel-shrink': {
                    background: theme.palette.background.default,
                  },
                }}
              >
                Period
              </InputLabel>
              <Select
                labelId="report-select-label"
                id="report-select"
                variant="outlined"
                value={String(selectedReportIndex)}
                label="Period"
                style={{ borderRadius: '21px' }}
                onChange={handleReportChange}
              >
                {reports.map((report, index) => (
                  <MenuItem key={report.periodId} value={String(index)}>
                    {getPeriodLabel(report.periodId)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      <EmployeeRewardsContent
        report={selectedReport}
        userName={userName}
        userId={userId || ''}
        periodLabel={selectedPeriodLabel}
        contentRef={contentRef}
        description={selectedReport?.package?.description}
        disclaimer={selectedReport?.package?.disclaimer}
        logoUrl={organization?.metadata?.logoBase64 || organization?.metadata?.logoUrl}
      />
    </>
  )
}

export default EmployeeRewardsDashboard
